package com.htmlagent.shared

/**
 * The single definition of the API routes: the `coRouter` in `:api` uses the templates
 * (`/{id}`) and the `:site` client builds the resolved URLs with the functions. Kept apart
 * they would drift silently, with a runtime 404 as the only symptom.
 */
object ApiRoutes {
    const val BASE = "/api"

    /**
     * Service liveness. It does not duplicate `/actuator/health`: that one checks
     * dependencies for the infrastructure; this one says which instance answered.
     */
    const val HEALTH = "$BASE/health"

    object Sessions {
        const val ROOT = "$BASE/sessions"

        const val ID_PARAM = "id"
        const val VERSION_PARAM = "version"
        const val BY_ID = "/{$ID_PARAM}"
        const val MESSAGES = "/{$ID_PARAM}/messages"
        const val MESSAGE_PARAM = "messageId"

        /**
         * One of the person's messages. `DELETE` rewinds the conversation to just before it;
         * `PATCH` does the same and sends the new text as a fresh turn.
         */
        const val MESSAGE = "/{$ID_PARAM}/messages/{$MESSAGE_PARAM}"
        const val POSITION_PARAM = "position"

        /** One of a message's reference photos, as the image itself. */
        const val MESSAGE_IMAGE = "/{$ID_PARAM}/messages/{$MESSAGE_PARAM}/images/{$POSITION_PARAM}"
        const val CANCEL = "/{$ID_PARAM}/cancel"
        const val VERSIONS = "/{$ID_PARAM}/versions"

        /** The code of a single version, as text. [VERSIONS] does not carry the HTML. */
        const val VERSION_CODE = "/{$ID_PARAM}/versions/{$VERSION_PARAM}"
        const val STREAM = "/{$ID_PARAM}/stream"

        fun byId(id: String): String = "$ROOT/$id"
        fun messages(id: String): String = "${byId(id)}/messages"
        fun message(id: String, messageId: Long): String = "${messages(id)}/$messageId"
        fun messageImage(id: String, messageId: Long, position: Int): String =
            "${message(id, messageId)}/images/$position"
        fun cancel(id: String): String = "${byId(id)}/cancel"
        fun versions(id: String): String = "${byId(id)}/versions"

        fun versionCode(id: String, version: Int): String = "${byId(id)}/versions/$version"
        /**
         * Asks the stream for what comes from now on only, with no replay. On reopening a
         * session the client already loaded the conversation with `GET /api/sessions/{id}`,
         * and the replay would duplicate the agent message. A new connection is not a
         * reconnection.
         */
        const val LIVE_PARAM = "live"

        fun stream(id: String): String = "${byId(id)}/stream"

        /** The stream without replay, for when the history was already loaded some other way. */
        fun liveStream(id: String): String = "${stream(id)}?$LIVE_PARAM=true"
    }

    /**
     * Signing in. The session travels in an `HttpOnly` cookie, never in a body the page can read:
     * `EventSource` cannot send an `Authorization` header, and a token in `localStorage` is one
     * script injection away from leaking.
     */
    object Auth {
        const val ROOT = "$BASE/auth"

        /** Emails an access link. */
        const val LINK = "/link"

        /** Trades an access link's token for a session cookie. */
        const val VERIFY = "/verify"

        /** Approves, from another browser, the one that asked for the link and is waiting. */
        const val APPROVE = "/approve"

        /** Asked by the browser waiting for its link: it signs in once the link is approved. */
        const val WAIT = "/wait"

        /** Opens a guest session, new or resumed with a recovery key. */
        const val GUEST = "/guest"

        /** Who the cookie belongs to. */
        const val ME = "/me"
        const val LOGOUT = "/logout"

        /** The query parameter that carries the token in the emailed link. */
        const val LINK_TOKEN_PARAM = "token"

        fun link(): String = "$ROOT$LINK"
        fun verify(): String = "$ROOT$VERIFY"
        fun approve(): String = "$ROOT$APPROVE"
        fun await(): String = "$ROOT$WAIT"
        fun guest(): String = "$ROOT$GUEST"
        fun me(): String = "$ROOT$ME"
        fun logout(): String = "$ROOT$LOGOUT"
    }

    /**
     * Artifacts. They do not hang from [BASE]: they are served from a separate origin
     * because the generated HTML is untrusted code, and a path on the same origin would
     * isolate nothing.
     */
    object Artifacts {
        const val ROOT = "/a"

        const val ID_PARAM = "artifactId"
        const val VERSION_PARAM = "version"
        const val BY_VERSION = "/{$ID_PARAM}/{$VERSION_PARAM}"
        const val DOWNLOAD = "/{$ID_PARAM}/{$VERSION_PARAM}/download"

        fun byVersion(artifactId: String, version: Int): String = "$ROOT/$artifactId/$version"
        fun download(artifactId: String, version: Int): String = "${byVersion(artifactId, version)}/download"
    }
}

